import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Formula } from "building-types/options/formula";
import type { EvaluatedRoofSlopeOptions, RoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import { RoofTruncationType, TruncationSlopeType } from "building-types/settings/roofBaseTypes";
import type {
  RoofSlope,
  NeighborRoof,
  NeighborRoofs,
  SlopeOverlapRange,
  RoofInputPerimeters,
  RoofPerimeters,
  RoofSystemLevel,
  SkylightOpening,
  Skylight,
  RoofTruncationOptions,
  RidgeRail,
  RoofRails,
  RoofRails2D,
  RoofConnectivity,
  RoofExtensions,
  ShedRoofExtensions,
  RoofOverhangs,
  RoofGableFaces,
  RectangularOverhangs,
  RoofGableVents,
  RoofMaterialTrimAndProps,
  EvaluatedRoofSideOverridesTable,
} from "building-types/settings/roofTypes";
import { RoofFaces, RoofSide } from "building-types/settings/roofTypes";
import type { RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import type { Face } from "geom/brep";
import type { NodeBSP } from "geom/bspTypes";
import type { Plane } from "geom/planeTypes";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";

export const MULTIGABLE_BSP_EPSILON = 1e-3;
export const FRIEZE_RAIL_SEARCH_BOX_TOLERANCE = 2;
export const MULTIGABLE_DEFAULT_SLOPE = 1;
export const MULTIGABLE_INFINITE_SLOPE = roofSlopeNew(Number.POSITIVE_INFINITY, 0, 0, 0, 0, Number.POSITIVE_INFINITY);
export const DEFAULT_VAULTED_ROOF_THICKNESS = 20;
export const OPENING_TRANSFER_DISTANCE_TOLERANCE = 1;
export const GABLE_END_FACE_OFFSET = -0.1;
export const DEFAULT_ROOF_TRUNCATION_SLOPE = 1;
export const ROOF_Z_FIGHTING_OFFSET = 0.05;

export const isSideRoofFace: {
  [key in RoofFaces]?: boolean;
} = {
  [RoofFaces.LEFT]: true,
  [RoofFaces.RIGHT]: true,
  [RoofFaces.LEFT_AND_RIGHT]: true,
};

export function roofFaceToString(face: RoofFaces) {
  if (face === undefined) {
    face = RoofFaces.FRONT_AND_BACK;
  }
  switch (face) {
    case RoofFaces.ALL: {
      return "All";
    }
    case RoofFaces.FRONT_AND_BACK: {
      return "Both Ends";
    }
    case RoofFaces.LEFT_AND_RIGHT: {
      return "Both Sides";
    }
    case RoofFaces.FRONT: {
      return "Front";
    }
    case RoofFaces.BACK: {
      return "Back";
    }
    case RoofFaces.LEFT: {
      return "Left";
    }
    case RoofFaces.RIGHT: {
      return "Right";
    }
    default: {
      assertUnreachable(face);
    }
  }
}

export const defaultRoofFasciaTrimOverrides = {
  includeFasciaTopTrim: true,
  fasciaTopTrimSize: 2,
};

export const defaultRoofSideOverrides: EvaluatedRoofSideOverridesTable = {
  [RoofSide.LEFT]: {},
  [RoofSide.RIGHT]: {},
  [RoofSide.START]: {},
  [RoofSide.END]: {},
};

export function neighborRoofNew(
  baseHeight: number,
  baseHeightOffset: number = 0,
  overhang: number = 0,
  startOverhang: number = 0,
  endOverhang: number = 0,
  relativeDist: number = 0,
  depth: number = 0,
  slopes: RoofSlope[] = [],
  oppSlopes: RoofSlope[] = [],
  roofId: string = "",
  side: RoofSide = RoofSide.INVALID,
  isTruncated: boolean = false,
): NeighborRoof {
  return {
    isTruncated,
    slopes,
    oppSlopes,
    roofId,
    side,
    baseHeight,
    baseHeightOffset,
    overhang,
    startOverhang,
    endOverhang,
    relativeDist,
    depth,
  };
}

export function neighborRoofsNew(
  startNeighbor: NeighborRoof,
  endNeighbor: NeighborRoof,
  leftNeighbor: NeighborRoof,
  rightNeighbor: NeighborRoof,
): NeighborRoofs {
  return {
    startNeighbor,
    endNeighbor,
    leftNeighbor,
    rightNeighbor,
  };
}

export function roofSlopeOptionsNew(slope: number | Formula, horizontalDist: number = -1.0): RoofSlopeOptions {
  return {
    slope,
    horizontalDist,
  };
}

export function evaluatedRoofSlopeOptionsNew(slope: number, horizontalDist: number = -1.0): EvaluatedRoofSlopeOptions {
  return {
    slope,
    horizontalDist,
  };
}

export function roofSlopeNew(
  slope: number,
  horizontalDist: number,
  startDist: number,
  endDist: number,
  startHeight: number,
  endHeight: number,
  hasStartOverlapping: boolean = true,
  hasEndOverlapping: boolean = true,
): RoofSlope {
  return {
    slope,
    horizontalDist,
    startDist,
    endDist,
    startHeight,
    endHeight,
    hasStartOverlapping,
    hasEndOverlapping,
  };
}

export function slopeOverlapRangeNew(t1: number, t2: number): SlopeOverlapRange {
  return {
    t1,
    t2,
  };
}

export function roofInputPerimetersNew(
  inputPerimeter: PolyLine2,
  srcPerimeter: PolyLine2,
  boundingPerimeter: PolyLine2,
  exactPerimeter: PolyLine2,
  bottomPerimeter: PolyLine2 | null,
  limitPerimeter: PolyLine2 | null = null,
  isVirtualRoof: boolean = false,
  isFloatingVirtualRoof: boolean = false,
): RoofInputPerimeters {
  return {
    inputPerimeter,
    srcPerimeter,
    boundingPerimeter,
    limitPerimeter,
    exactPerimeter,
    bottomPerimeter,
    isVirtualRoof,
    isFloatingVirtualRoof,
  };
}

export function roofPerimetersNew(
  startEdge: number,
  peakDirection: RoofPeakDirection | undefined,
  inputPerimeter: PolyLine2,
  boundingPerimeter: PolyLine2,
  offsetBoundingPerimeter: PolyLine2,
  exactPerimeter: PolyLine2,
  offsetExactPerimeter: PolyLine2,
  wallPerimeter: PolyLine2,
  offsetWallPerimeter: PolyLine2,
  eavesPerimeter: PolyLine2,
  wallsPerimeterWRTOverhangDatum: PolyLine2,
): RoofPerimeters {
  return {
    startEdge,
    peakDirection,
    inputPerimeter,
    boundingPerimeter,
    offsetBoundingPerimeter,
    exactPerimeter,
    offsetExactPerimeter,
    wallPerimeter,
    offsetWallPerimeter,
    eavesPerimeter,
    wallsPerimeterWRTOverhangDatum,
  };
}

export function multiGableRoofLevelNew(
  upperFloorBSP: NodeBSP | null,
  upperFloorRoomsBSP: NodeBSP | null,
  halfStoriesBSP: NodeBSP | null,
  upperFloorPerimeters: Table<PolyLine2[]>,
  upperFloorCeilElevation: number | undefined,
  subGableRoofsPerimeters: Table<RoofInputPerimeters>,
  subGableRoofsPlanarGraph: PolygonPlanarGraph,
  subGableRoofsExactPlanarGraph: PolygonPlanarGraph,
  subGableRoofIdToPolygonIndex: Table<number>,
  subGablePolygonIndexToRoofId: Table<string>,
): RoofSystemLevel {
  return {
    upperFloorBSP,
    upperFloorRoomsBSP,
    halfStoriesBSP,
    upperFloorPerimeters,
    upperFloorCeilElevation,
    roofPerimeters: subGableRoofsPerimeters,
    roofPlanarGraphs: subGableRoofsPlanarGraph,
    roofExactPlanarGraph: subGableRoofsExactPlanarGraph,
    roofIdToPolygonIndex: subGableRoofIdToPolygonIndex,
    roofPolygonIndexToRoofId: subGablePolygonIndexToRoofId,
  };
}

export function skylightNew(
  pickHandle: DocEntityHandle,
  hostSubRoofId: string,
  levelId: string,
  width: number,
  height: number,
  verticalOffset: number,
  param: number,
  paramNormalized?: boolean,
  opening?: SkylightOpening,
): Skylight {
  return {
    pickHandle,
    hostSubRoofId,
    levelId,
    width,
    height,
    verticalOffset,
    param,
    paramNormalized,
    opening,
  };
}

export function roofTruncationOptionsNew(
  type: RoofTruncationType = RoofTruncationType.NONE,
  heightFactor: number = 0.5,
  normalizedHeight: boolean = true,
  slopeType: TruncationSlopeType = TruncationSlopeType.SIDE_SLOPES,
  slopeOptions: RoofSlopeOptions[] = [roofSlopeOptionsNew(DEFAULT_ROOF_TRUNCATION_SLOPE)],
): RoofTruncationOptions {
  return {
    type,
    heightFactor,
    normalizedHeight,
    slopeType,
    slopeOptions,
  };
}

export function ridgeRailNew(
  rail: PolyLine3,
  ventProfile: PolyLine2,
  capProfile: PolyLine2,
  leftSlope: number,
  rightSlope: number,
  baseHeight: number,
  isHorizontal: boolean,
  addRidgeVent: boolean,
  clippingPlanes: Plane[] = [],
): RidgeRail {
  return {
    rail,
    ventProfile,
    capProfile,
    clippedRails: [rail],
    ridgeVentRails: isHorizontal ? [rail] : [],
    leftSlope,
    rightSlope,
    baseHeight,
    isHorizontal,
    addRidgeVent,
    clippingPlanes,
  };
}

export function roofRailsNew(
  leftRail: PolyLine3,
  rightRail: PolyLine3,
  startRail: PolyLine3,
  endRail: PolyLine3,
  peakRail: PolyLine3,
  peakRailNoExtension: PolyLine3,
  ridgeRails: RidgeRail[] = [],
  returnEaveRails: PolyLine3[] = [],
  returnPeakRails: PolyLine3[] = [],
): RoofRails {
  return {
    leftRail,
    rightRail,
    startRail,
    endRail,
    peakRail,
    peakRailNoExtension,
    ridgeRails,
    returnEaveRails,
    returnPeakRails,
  };
}

export function roofRails2DNew(
  leftRail: PolyLine2,
  rightRail: PolyLine2,
  startRail: PolyLine2,
  endRail: PolyLine2,
  peakRail: PolyLine2,
  peakRailNoExtension: PolyLine2,
): RoofRails2D {
  return {
    leftRail,
    rightRail,
    startRail,
    endRail,
    peakRail,
    peakRailNoExtension,
  };
}

export function roofConnectivityNew({
  isFree = false,
  isTouchingBuilding = false,
  hasPartialOverlap = false,
}: Partial<RoofConnectivity>): RoofConnectivity {
  return {
    isFree,
    isTouchingBuilding,
    hasPartialOverlap,
  };
}

export function roofExtensionsNew({
  hasStartExtension = false,
  hasEndExtension = false,
  hasStartPartialOverlap = false,
  hasEndPartialOverlap = false,
  startLeftExtensions = [],
  startRightExtensions = [],
  endLeftExtensions = [],
  endRightExtensions = [],
}: Partial<RoofExtensions>): RoofExtensions {
  return {
    hasStartExtension,
    hasEndExtension,
    hasStartPartialOverlap,
    hasEndPartialOverlap,
    startLeftExtensions,
    startRightExtensions,
    endLeftExtensions,
    endRightExtensions,
  };
}

export function shedRoofExtensionsNew({
  hasLeftExtension = false,
  hasRightExtension = false,
  sideExtension = 0,
}: Partial<ShedRoofExtensions>): ShedRoofExtensions {
  return {
    hasLeftExtension,
    hasRightExtension,
    sideExtension,
  };
}

export function roofOverhangsNew({
  edgeOverhangs = [],
  baseEdgeOverhangs = [],
  limitEdgeOverhangs = [],
  limitEdgeNoOverhangs = [],
}: Partial<RoofOverhangs>): RoofOverhangs {
  return {
    edgeOverhangs,
    baseEdgeOverhangs,
    limitEdgeOverhangs,
    limitEdgeNoOverhangs,
  };
}

export function roofGableFacesNew(
  plane: Plane | null = null,
  face: Face | null = null,
  discardFace: Face | null = null,
): RoofGableFaces {
  return {
    plane,
    face,
    discardFace,
  };
}

export function rectangularOverhangsNew(
  startOverhang: number = 0,
  endOverhang: number = 0,
  leftOverhang: number = 0,
  rightOverhang: number = 0,
  baseStartOverhang: number = 0,
  baseEndOverhang: number = 0,
  baseLeftOverhang: number = 0,
  baseRightOverhang: number = 0,
): RectangularOverhangs {
  return {
    startOverhang,
    endOverhang,
    leftOverhang,
    rightOverhang,
    baseStartOverhang,
    baseEndOverhang,
    baseLeftOverhang,
    baseRightOverhang,
  };
}

export function roofGableVentsNew(
  includeStartTriangleGableVent?: boolean,
  startTriangleGableVentBaseWidth?: number,
  includeEndTriangleGableVent?: boolean,
  endTriangleGableVentBaseWidth?: number,
): RoofGableVents {
  return {
    includeStartTriangleGableVent,
    startTriangleGableVentBaseWidth,
    includeEndTriangleGableVent,
    endTriangleGableVentBaseWidth,
  };
}

export function roofMaterialAndTrimPropsNew({
  includeFasciaTopTrim,
  fasciaTopTrimSize,
  fasciaTopTrimMaterial,
  friezeBoard,
  friezeBoardMaterialLegacy,
  wrapFriezeBoardMode,
  includeRakeBrackets,
  rakeBracketPropId,
  rakeBracketAtPeak,
  rakeBracketParams,
  rakeBracketParamsNormalized,
  rakeBracketTruncationParams,
  soffitMaterial,
  fasciaMaterial,
  fasciaSize,
  soffitOffset,
  includeEaveBrackets,
  eaveBracketPropId,
  eaveBracketParams,
  eaveBracketParamsNormalized,
  eaveBracketPlacement,
}: RoofMaterialTrimAndProps): RoofMaterialTrimAndProps {
  return {
    includeFasciaTopTrim,
    fasciaTopTrimSize,
    fasciaTopTrimMaterial,
    friezeBoard,
    friezeBoardMaterialLegacy,
    wrapFriezeBoardMode,
    includeRakeBrackets,
    rakeBracketPropId,
    rakeBracketAtPeak,
    rakeBracketParams,
    rakeBracketParamsNormalized,
    rakeBracketTruncationParams,
    soffitMaterial,
    fasciaMaterial,
    fasciaSize,
    soffitOffset,
    includeEaveBrackets,
    eaveBracketPropId,
    eaveBracketParams,
    eaveBracketParamsNormalized,
    eaveBracketPlacement,
  };
}
